/**
 * Host and navigation hardening helpers for FE-SP-002A.
 *
 * - strict allowlist for app hosts
 * - safe local return-path extraction for login/route restores
 * - canonical host redirect target builder
 */

import type { ReactNode } from "react";

export const APP_HOSTS = ["app.benecrm.com", "app.getshortpay.com"] as const;
const APP_HOSTS_LIST = APP_HOSTS as readonly string[];

export type AppShell = "shortpay" | "benecrm";
export type ReturnPathState = { from?: string | null } | undefined;

export interface AppShellState {
  shell: AppShell | null;
  path: string;
}

export const DEFAULT_AUTH_RETURN_PATH = "/dashboard";

export function shellHome(shell: AppShell): string {
  return shell === "shortpay" ? "/shortpay" : DEFAULT_AUTH_RETURN_PATH;
}

// The complete set of authenticated Shortpay product routes. This is the
// single shared source of truth consumed by App.tsx route mounting, the
// AppShell product navigation, login return-path ownership, and the tests that
// compare all three against it. Keep every routed Shortpay path here so
// unauthenticated deep links to a surfaced route restore to that exact path
// after login instead of falling back to the workspace home. Sales CRM is a
// BeneCRM product, not Shortpay, so it is deliberately absent.
export const SHORTPAY_ROUTES = [
  "/shortpay",
  "/shortpay/onboarding",
  "/shortpay/statements",
  "/shortpay/matching",
  "/shortpay/exceptions",
  "/shortpay/recovery",
  "/shortpay/settings",
] as const;

export type ShortpayRoute = (typeof SHORTPAY_ROUTES)[number];

// Product-navigation labels keyed by registry route so the shell nav is
// derived from the same single source of truth as route ownership.
export const SHORTPAY_NAV_LABELS: Record<ShortpayRoute, string> = {
  "/shortpay": "Dashboard",
  "/shortpay/onboarding": "Onboarding",
  "/shortpay/statements": "Statements",
  "/shortpay/matching": "Matching",
  "/shortpay/exceptions": "Exceptions",
  "/shortpay/recovery": "Recovery",
  "/shortpay/settings": "Settings",
};

export const SHORTPAY_NAV_LINKS: ReadonlyArray<{ to: ShortpayRoute; label: string }> =
  SHORTPAY_ROUTES.map((route) => ({ to: route, label: SHORTPAY_NAV_LABELS[route] }));

export function isRouteOwnedByShell(pathname: string, shell: AppShell): boolean {
  const routes = shell === "shortpay" ? SHORTPAY_ROUTES : ["/dashboard"];
  return routes.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

function normalizeHost(hostname: string | null | undefined): string | null {
  if (!hostname) return null;
  return hostname.trim().toLowerCase();
}

export function isKnownAppHost(hostname: string | null | undefined): boolean {
  const normalized = normalizeHost(hostname);
  if (!normalized) return false;
  return APP_HOSTS_LIST.includes(normalized);
}

export function shellFromHostname(hostname: string | null | undefined): AppShell {
  const normalized = normalizeHost(hostname);
  if (!normalized) return "benecrm";
  if (normalized === "app.getshortpay.com") return "shortpay";
  if (normalized === "app.benecrm.com") return "benecrm";
  return "benecrm";
}

export function currentShellFromLocation(
  locationLike: { hostname: string } =
    (typeof window === "undefined" ? { hostname: "app.benecrm.com" } : window.location),
): AppShell {
  return shellFromHostname(locationLike.hostname);
}

const MAX_RETURN_PATH_DECODE_PASSES = 8;
const CONTROL_CHARACTER_PATTERN = /[\u0000-\u001F\u007F-\u009F\u2028\u2029]/;
const ENCODED_BYTE_RUN_PATTERN = /(?:%[0-9A-Fa-f]{2})+/g;

function decodeEncodedSafetyLayer(value: string): { value: string; changed: boolean } {
  let changed = false;
  const decoded = value.replace(ENCODED_BYTE_RUN_PATTERN, (encodedRun) => {
    try {
      const next = decodeURIComponent(encodedRun);
      changed ||= next !== encodedRun;
      return next;
    } catch {
      // A percent sign produced by a valid %25 literal may be followed by
      // ordinary hex-looking text. Leave that literal untouched; the
      // original input was already validated strictly below.
      return encodedRun;
    }
  });
  return { value: decoded, changed };
}

function fullyDecodeReturnPath(value: string): string | null {
  let decoded: string;
  try {
    // Validate the original URL encoding strictly. This rejects malformed
    // percent triplets and invalid UTF-8 before any tolerant safety pass.
    decoded = decodeURIComponent(value);
  } catch {
    return null;
  }

  for (let pass = 0; pass < MAX_RETURN_PATH_DECODE_PASSES; pass += 1) {
    const layer = decodeEncodedSafetyLayer(decoded);
    if (!layer.changed) return decoded;
    decoded = layer.value;
    if (hasUnsafeDecodedPath(decoded)) return null;
  }

  // A destination that still has another decodable layer after the bound is
  // unsafe to partially trust. Literal percent characters are not matched by
  // the safety-layer decoder and therefore do not trigger this rejection.
  return decodeEncodedSafetyLayer(decoded).changed ? null : decoded;
}

function hasUnsafeDecodedPath(decoded: string): boolean {
  const decodedPathname = decoded.split(/[?#]/, 1)[0] || "/";
  if (
    CONTROL_CHARACTER_PATTERN.test(decoded)
    || decoded.includes("\\")
    || decoded.startsWith("//")
    || decodedPathname.includes("://")
  ) {
    return true;
  }

  const pathSegments = decodedPathname.split("/");
  return pathSegments.some((segment) => segment === "." || segment === "..")
    || decodedPathname.includes("//");
}

export function sanitizeReturnPath(
  value: string,
  fallback: string = DEFAULT_AUTH_RETURN_PATH,
  shell?: AppShell,
): string {
  if (typeof value !== "string") return fallback;

  const trimmed = value.trim();
  if (!trimmed.startsWith("/")) return fallback;

  const decoded = fullyDecodeReturnPath(trimmed);
  if (!decoded || hasUnsafeDecodedPath(decoded)) return fallback;

  const protocolLike = /^[a-z][a-z0-9+.-]*:/i;
  if (protocolLike.test(decoded) || protocolLike.test(decoded.slice(1))) {
    return fallback;
  }

  const pathname = decoded.split(/[?#]/, 1)[0] || "/";
  if (shell && !isRouteOwnedByShell(pathname, shell)) return fallback;
  return trimmed;
}

export function parseReturnPathFromState(
  state: unknown,
  fallback: string = DEFAULT_AUTH_RETURN_PATH,
  shell?: AppShell,
): string {
  if (typeof state === "string") {
    return sanitizeReturnPath(state, fallback, shell);
  }

  const candidate = state as ReturnPathState | null | undefined;
  if (!candidate || typeof candidate !== "object") {
    return fallback;
  }

  if (typeof candidate.from === "string") {
    return sanitizeReturnPath(candidate.from, fallback, shell);
  }

  return fallback;
}

function parseRedirectHost(raw: string | null | undefined): string | null {
  if (!raw) return null;

  try {
    const normalized = raw.toLowerCase().trim();
    if (normalized === "") return null;
    if (APP_HOSTS_LIST.includes(normalized)) return normalized;
    const parsed = normalized.includes("://")
      ? new URL(normalized)
      : new URL(`https://${normalized}`);
    if (
      !["http:", "https:"].includes(parsed.protocol)
      || parsed.username
      || parsed.password
      || parsed.port
      || parsed.pathname !== "/"
      || parsed.search
      || parsed.hash
    ) return null;
    return parsed.hostname.toLowerCase();
  } catch {
    return null;
  }
}

// Validates a single canonical-redirect URL component (pathname, search, or
// hash) so a canonical navigation can never hand window.location.replace a URL
// carrying control characters, malformed percent encodings, encoded
// protocol-relative or traversal forms, backslashes, or protocol-like injected
// values. Valid encoded paths (e.g. a literal %25) are preserved. `position`
// distinguishes the pathname (where a leading scheme can be authority-like)
// from search/hash (whose leading character is the ? or # delimiter).
function isSafeCanonicalComponent(
  value: string,
  position: "path" | "rest",
): boolean {
  if (value === "") return true;

  // Strictly validate the source URL encoding: reject malformed percent
  // triplets and invalid UTF-8 before any tolerant safety decode.
  try {
    decodeURIComponent(value);
  } catch {
    return false;
  }

  // Bounded multi-pass decode rejects double-encoded hostile layers and any
  // decoded form with control characters, backslashes, leading protocol-
  // relative //, "scheme:" authority-like injection, or . / .. traversal.
  const decoded = fullyDecodeReturnPath(value);
  if (!decoded) return false;

  // fullyDecodeReturnPath only runs the unsafe check when an extra decode
  // layer actually changed; single-layer-decoded traversal/backslash (e.g.
  // %2e%2e, %5c) must still fail closed.
  if (hasUnsafeDecodedPath(decoded)) return false;

  if (decoded.startsWith("//")) return false;
  const protocolLike = /^[a-z][a-z0-9+.-]*:/i;
  if (protocolLike.test(decoded)) return false;
  if (position === "path" && protocolLike.test(decoded.slice(1))) return false;

  return true;
}

export function canonicalRedirectHref(
  canonicalAppDomain: string | null | undefined,
  opts: {
    host?: string;
    protocol?: string;
    pathname?: string;
    search?: string;
    hash?: string;
  } = {},
): string | null {
  const currentHost = opts.host && isKnownAppHost(opts.host)
    ? normalizeHost(opts.host)
    : null;
  if (!currentHost) return null;

  const canonicalHost = parseRedirectHost(canonicalAppDomain);
  if (!canonicalHost || !isKnownAppHost(canonicalHost)) return null;
  if (canonicalHost === currentHost) return null;

  const pathname = opts.pathname ?? "/";
  if (!pathname.startsWith("/") || !isSafeCanonicalComponent(pathname, "path")) {
    return null;
  }
  const search = opts.search ?? "";
  if (!isSafeCanonicalComponent(search, "rest")) return null;
  const hash = opts.hash ?? "";
  if (!isSafeCanonicalComponent(hash, "rest")) return null;
  const protocol = opts.protocol === "http:" || opts.protocol === "https:"
    ? opts.protocol
    : "https:";

  return `${protocol}//${canonicalHost}${pathname}${search}${hash}`;
}

export function navigateToCanonicalHost(
  canonicalAppDomain: string | null | undefined,
  opts: Parameters<typeof canonicalRedirectHref>[1] = {},
  navigate: (href: string) => void = (href) => window.location.replace(href),
): boolean {
  const href = canonicalRedirectHref(canonicalAppDomain, opts);
  if (!href) return false;
  navigate(href);
  return true;
}

export function parseShellFromReconMe(
  shell?: string | null,
  canonicalAppDomain?: string | null,
): AppShell {
  return shell === "shortpay" && canonicalAppDomain === "app.getshortpay.com"
    ? "shortpay"
    : "benecrm";
}

export function fallbackShellState(pathname = DEFAULT_AUTH_RETURN_PATH): AppShellState {
  return {
    shell: currentShellFromLocation(),
    path: pathname,
  };
}

export function shellNavigationHint(
  shell: AppShell,
  isMobile: boolean,
): ReactNode {
  if (shell === "shortpay") {
    return isMobile ? "Shortpay" : "Shortpay Workspace";
  }
  return isMobile ? "BeneCRM" : "BeneCRM Workspace";
}
